import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Award, Star, AlertTriangle, CheckCircle2, Trophy, Camera, Download, Loader2, User, Eye, CalendarClock, Ban, FileDown } from 'lucide-react';
import EscolherAnexo from '../../EscolherAnexo';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// RH — DESTAQUE DO MÊS (tela)
// Lista, por loja, os funcionários exemplares (sem falta, atestado ou atraso, ativos,
// fora da gestão, +60 dias de casa) e deixa o gerente ELEGER os destaques (2 por loja;
// 4 nas 24h ANV/VEL/SED). A eleição é DEFINITIVA.
//
// ⚠️ FOTO E PROVA SÃO ANEXADAS NA HORA DE ELEGER (2026-08-06). Antes o fluxo era
//    "eleger → anexar depois" e metade dos destaques de julho ficou sem prova; como a
//    eleição é definitiva, não havia conserto. Agora os dois arquivos viajam no mesmo
//    POST e o botão só habilita quando todos os escolhidos estão completos.
//
// Quatro regras governam a tela (todas do backend, espelhadas aqui só para a UI):
//  • o módulo começa em JULHO/2026 — o filtro de período não oferece mês anterior;
//  • a eleição só abre do dia 1º às 12h até o dia 3 às 12h (para todo perfil);
//  • o espelho de ponto do mês tem de estar COMPLETO — mês pela metade não elege;
//  • o RH pode ENCERRAR a eleição do mês antes do prazo — aí ninguém mais elege
//    (o estado vem do backend em `eleicao`; o botão de encerrar é só do RH central).
//
// Duas vistas:
//  • ANTES de eleger  → elegíveis em DOIS blocos (mês selecionado + prévia do mês atual
//    em andamento) + quem está em carência. Sem a lista de não-elegíveis.
//  • DEPOIS de eleger → só os cards dos destaques com as fotos (some o resto).
// O backend é a autoridade da elegibilidade, do recorte por loja e dos anexos.

const MESES = [
    { v: 1, nome: 'Janeiro' }, { v: 2, nome: 'Fevereiro' }, { v: 3, nome: 'Março' },
    { v: 4, nome: 'Abril' }, { v: 5, nome: 'Maio' }, { v: 6, nome: 'Junho' },
    { v: 7, nome: 'Julho' }, { v: 8, nome: 'Agosto' }, { v: 9, nome: 'Setembro' },
    { v: 10, nome: 'Outubro' }, { v: 11, nome: 'Novembro' }, { v: 12, nome: 'Dezembro' },
];

// Primeiro mês que o módulo apura — o filtro não oferece nada antes disso (o backend
// também recusa; ver PRIMEIRO_ANOMES em server/rh/destaque-mes-regras.js).
const PRIMEIRO_ANO = 2026;
const PRIMEIRO_MES = 7; // julho/2026

// Janela mensal da eleição: dia 1º a partir das 12h, o dia 2 inteiro e o dia 3 até as 12h.
// Espelha a regra do backend (destaque-mes-regras.js) — aqui só para esconder o botão e
// explicar o prazo; quem decide é o servidor (o relógio do navegador não é confiável).
const JANELA_TEXTO = 'do dia 1º às 12h até o dia 3 às 12h';
// ⚠️ EXCEÇÃO DATADA (pedido da Julia, 2026-11-03): só em nov/2026 o dia 3 fecha às 16h.
// Espelha EXCECAO_JANELA_NOV2026 do backend (quem decide é o servidor); remover depois
// de novembro/2026. Fora de nov/2026 não tem efeito. Nome datado: só o horaFim muda.
const EXCECAO_JANELA_NOV2026 = { ano: 2026, mes: 11, horaFim: 16 };
const janelaEleicaoAberta = (agora = new Date()) => {
    const dia = agora.getDate();
    if (dia === 1) return agora.getHours() >= 12;   // dia 1º: abre às 12h
    if (dia === 2) return true;                     // dia 2: aberto o dia todo
    if (dia === 3) {                                // dia 3: fecha às 12h (16h por exceção em nov/2026)
        const ex = EXCECAO_JANELA_NOV2026;
        const horaFim = (agora.getFullYear() === ex.ano && agora.getMonth() + 1 === ex.mes) ? ex.horaFim : 12;
        return agora.getHours() < horaFim;
    }
    return false;
};

// Lojas operacionais que elegem destaque (sem escritório; PCI/PCS já consolidados).
const LOJAS = ['ANV', 'BVV', 'FER', 'GTV', 'MAL', 'MOX', 'PCI', 'PCS', 'RDP', 'SED', 'SVV', 'VEL', 'VSH'];
const LOJAS_24H = ['ANV', 'VEL', 'SED']; // 24h → elegem 4 (SED entrou em 2026-07-23)
const PERFIS_PRIVILEGIADOS = ['Admin', 'RH', 'Diretoria'];

const limiteDestaques = (loja) => (LOJAS_24H.includes(String(loja || '').toUpperCase()) ? 4 : 2);

// Normaliza a loja do usuário para casar com o que o backend grava (PCI P&M → PCI).
const normLoja = (l) => {
    const s = String(l || '').trim().toUpperCase();
    if (s.startsWith('PCI')) return 'PCI';
    if (s.startsWith('PCS')) return 'PCS';
    return s;
};

// 'YYYY-MM' a partir de {ano, mes}.
const paraAnomes = (ano, mes) => `${ano}-${String(mes).padStart(2, '0')}`;

// Mês anterior fechado, como {ano, mes} — default da tela. Nunca abaixo do piso: em
// julho/2026 o mês anterior seria junho, que o módulo não apura (cai em julho, que
// aparece então como "mês em andamento").
const mesAnteriorInicial = () => {
    const d = new Date();
    const ant = new Date(d.getFullYear(), d.getMonth() - 1, 1);
    const ano = ant.getFullYear();
    const mes = ant.getMonth() + 1;
    if (ano < PRIMEIRO_ANO || (ano === PRIMEIRO_ANO && mes < PRIMEIRO_MES)) {
        return { ano: PRIMEIRO_ANO, mes: PRIMEIRO_MES };
    }
    return { ano, mes };
};

// Anos do filtro: do piso (2026) até o ano corrente, do mais novo para o mais velho.
// Ano futuro sai da lista — não há espelho de ponto do que ainda não aconteceu.
const anosDisponiveis = () => {
    const atual = Math.max(new Date().getFullYear(), PRIMEIRO_ANO);
    const anos = [];
    for (let a = atual; a >= PRIMEIRO_ANO; a -= 1) anos.push(a);
    return anos;
};

// Meses do filtro para o ano escolhido: no ano do piso, só de julho em diante.
const mesesDisponiveis = (ano) => (
    Number(ano) === PRIMEIRO_ANO ? MESES.filter((m) => m.v >= PRIMEIRO_MES) : MESES
);

// Mês atual (em andamento) como 'YYYY-MM' — usado no bloco de prévia.
const mesAtualAnomes = () => {
    const d = new Date();
    return paraAnomes(d.getFullYear(), d.getMonth() + 1);
};

// Redimensiona a imagem escolhida para no máx. `maxDim` px e devolve um data URI
// JPEG (~qualidade 0.85). Mantém o upload pequeno (o backend rejeita acima de 2 MB)
// sem exigir foto tratada do gerente.
function lerImagemRedimensionada(file, maxDim = 1280, quality = 0.85) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
        reader.onload = () => {
            const img = new Image();
            img.onerror = () => reject(new Error('O arquivo não é uma imagem válida.'));
            img.onload = () => {
                let { width, height } = img;
                if (width > maxDim || height > maxDim) {
                    const escala = Math.min(maxDim / width, maxDim / height);
                    width = Math.round(width * escala);
                    height = Math.round(height * escala);
                }
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                resolve(canvas.toDataURL('image/jpeg', quality));
            };
            img.src = reader.result;
        };
        reader.readAsDataURL(file);
    });
}

// Lê o arquivo CRU como data URI (sem canvas/resize) — usado para PDF/documento da
// prova, que não passa pelo redimensionamento de imagem. O backend limita a 5 MB.
function lerArquivoComoDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Falha ao ler o arquivo.'));
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
    });
}

// Prepara o anexo da PROVA: se for imagem, reaproveita o resize (upload pequeno);
// se for PDF/documento, envia cru. Devolve o data URI pronto pro POST /prova.
async function prepararAnexoProva(file) {
    if (file.type && file.type.startsWith('image/')) return lerImagemRedimensionada(file);
    return lerArquivoComoDataUrl(file);
}

const RHDestaqueMes = () => {
    const user = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; }
    }, []);
    const ehPrivilegiado = PERFIS_PRIVILEGIADOS.includes(perfilNoModulo(user?.perfil, 'rh'));
    const lojaDoUsuario = normLoja(user?.loja);

    const inicial = mesAnteriorInicial();
    const [ano, setAno] = useState(inicial.ano);
    const [mes, setMes] = useState(inicial.mes);
    // Loja em foco: privilegiado escolhe (''=todas); gerente fica preso à própria.
    const [lojaFiltro, setLojaFiltro] = useState(ehPrivilegiado ? '' : lojaDoUsuario);

    const [populacao, setPopulacao] = useState([]);       // mês selecionado
    const [populacaoAtual, setPopulacaoAtual] = useState([]); // prévia do mês atual
    const [eleitos, setEleitos] = useState([]);
    // Lojas que fecharam o mês SEM destaque (decisão registrada, também definitiva).
    const [semEleito, setSemEleito] = useState([]);
    const [selecionados, setSelecionados] = useState(new Set());
    const [carregando, setCarregando] = useState(false);
    const [salvando, setSalvando] = useState(false);
    // Cobertura do espelho no mês selecionado (backend): { diasNoMes, diasCarregados,
    // completo, faltando }. Mês incompleto BLOQUEIA a eleição — ver o banner abaixo.
    const [cobertura, setCobertura] = useState(null);
    const [coberturaAviso, setCoberturaAviso] = useState('');
    // Encerramento antecipado da eleição do mês (ato do RH central):
    // { encerrada, em, por, motivo }. Encerrado → nenhuma loja elege, mesmo no prazo.
    const [eleicao, setEleicao] = useState(null);
    const [eleicaoAviso, setEleicaoAviso] = useState('');
    const [alterandoEleicao, setAlterandoEleicao] = useState(false);
    // Anexos escolhidos ANTES de eleger, por ID_FUNCIONARIO:
    //   { [id]: { foto, fotoNome, prova, provaNome } }  (foto/prova = data URI base64)
    // Ficam só na memória da tela até o POST /eleger — nada sobe ao SharePoint antes.
    const [anexos, setAnexos] = useState({});
    // Prévia do TXT do POPULIS (só RH/Admin/Diretoria).
    const [previaPopulis, setPreviaPopulis] = useState(null);
    // Fila de exceções aguardando decisão do ESCRITORIO (DEM-00064).
    const [excecoesFila, setExcecoesFila] = useState([]);

    const anomes = paraAnomes(ano, mes);
    const mesAtual = mesAtualAnomes();
    const selecionadoEhAtual = anomes === mesAtual;
    // Eleição só faz sentido com UMA loja em foco (gerente sempre; privilegiado ao escolher).
    const lojaEmFoco = ehPrivilegiado ? lojaFiltro : lojaDoUsuario;
    // Prazo da eleição: só nos 3 primeiros dias do mês (dia 1º a partir das 12h).
    const janelaAberta = janelaEleicaoAberta();
    // Exceção datada: em nov/2026 a janela do dia 3 vai até as 16h — avisa no banner.
    const hojeD = new Date();
    const janelaExcecaoEsseMes = hojeD.getFullYear() === EXCECAO_JANELA_NOV2026.ano
        && hojeD.getMonth() + 1 === EXCECAO_JANELA_NOV2026.mes;
    // Espelho do mês carregado por inteiro? Enquanto o backend não responde (null), não
    // presumimos incompleto — só o `false` explícito bloqueia.
    const espelhoCompleto = cobertura ? cobertura.completo : true;
    // Eleição encerrada pelo RH antes do prazo? Enquanto o backend não responde (null),
    // não presumimos encerrada — só o `true` explícito bloqueia.
    const eleicaoEncerrada = Boolean(eleicao && eleicao.encerrada);
    // Mês em andamento não elege (espelho parcial) — só o mês fechado, dentro do prazo,
    // com o espelho inteiro e a eleição do mês ainda aberta.
    const podeEleger = Boolean(lojaEmFoco) && !selecionadoEhAtual && janelaAberta
        && espelhoCompleto && !eleicaoEncerrada;
    // Vagas que ainda restam na loja: o teto menos quem já foi eleito no mês. É o que
    // sustenta o COMPLEMENTO (DEM-00063) — eleger mais até o teto enquanto a janela
    // estiver aberta, sem mexer em quem fechou o mês sem destaque.
    const vagas = lojaEmFoco ? Math.max(0, limiteDestaques(lojaEmFoco) - eleitos.length) : null;
    // Encerrar/reabrir é do RH central, e só faz sentido em mês fechado com a janela
    // aberta: fora dela a própria janela já barra a eleição, e não há o que encerrar.
    const podeEncerrar = ehPrivilegiado && !selecionadoEhAtual && janelaAberta;
    const podeFoto = Boolean(lojaEmFoco); // quem tem loja em foco anexa/troca a foto
    // Com a loja em foco, "já decidiu" = elegeu alguém OU fechou o mês sem destaque.
    const jaSemEleito = semEleito.length > 0;
    const jaEleito = eleitos.length > 0 || jaSemEleito;
    // Pode (continuar a) eleger? Mesmas travas de sempre + a loja não fechou "sem
    // destaque" e ainda há vaga. Cobre a 1ª eleição E o complemento de uma já feita.
    const podeMarcarMais = podeEleger && !jaSemEleito && vagas > 0;
    // Vista "só cards": loja em foco, já há decisão e NÃO dá para complementar mais.
    const vistaSoCards = Boolean(lojaEmFoco) && jaEleito && !podeMarcarMais;

    // EXCEÇÃO (DEM-00064). Quem APROVA é usuário do ESCRITORIO (ou Admin) — a autoridade
    // é por LOJA, não por perfil. Quem MARCA é o gerente, nas mesmas condições de eleger
    // (mês fechado, janela aberta, espelho inteiro): a exceção só serve para então eleger.
    const ehEscritorio = user?.perfil === 'Admin' || normLoja(user?.loja) === 'ESCRITORIO';
    const podeMarcarExcecao = podeEleger;

    const buscar = useCallback(async () => {
        setCarregando(true);
        setSelecionados(new Set());
        setAnexos({}); // trocou de mês/loja: os arquivos escolhidos não valem mais
        try {
            const token = localStorage.getItem('authToken');
            if (!token) throw new Error('Sessão expirada. Faça login novamente.');
            const headers = { Authorization: `Bearer ${token}` };
            const lojaQS = ehPrivilegiado && lojaFiltro ? lojaFiltro : null;

            const qs = (m) => {
                const p = new URLSearchParams({ mes: m });
                if (lojaQS) p.set('loja', lojaQS);
                return p;
            };
            // Mês selecionado (consulta + eleitos) e, se diferente, a prévia do mês atual.
            const chamadas = [
                fetch(`/api/rh/destaque-mes/consulta?${qs(anomes)}`, { headers }),
                fetch(`/api/rh/destaque-mes/eleitos?${qs(anomes)}`, { headers }),
            ];
            if (!selecionadoEhAtual) {
                chamadas.push(fetch(`/api/rh/destaque-mes/consulta?${qs(mesAtual)}`, { headers }));
            }
            const [rCons, rEle, rAtual] = await Promise.all(chamadas);

            if (!rCons.ok) throw new Error((await rCons.json().catch(() => ({}))).message || `Erro ${rCons.status}`);
            const dados = await rCons.json();
            const jEle = rEle.ok ? await rEle.json() : {};
            const ele = jEle.eleitos || [];
            const atual = rAtual && rAtual.ok ? (await rAtual.json()).populacao || [] : [];

            setPopulacao(dados.populacao || []);
            setEleitos(ele);
            setSemEleito(jEle.semEleito || []);
            setPopulacaoAtual(atual);
            setCobertura(dados.cobertura || null);
            setCoberturaAviso(dados.coberturaAviso || '');
            setEleicao(dados.eleicao || null);
            setEleicaoAviso(dados.eleicaoAviso || '');

            // Prévia do TXT do POPULIS: só quem exporta (RH/Admin/Diretoria) e só para
            // mês fechado. Falha aqui não derruba a tela — é bloco acessório.
            if (ehPrivilegiado && !selecionadoEhAtual) {
                try {
                    const rp = await fetch(`/api/rh/destaque-mes/populis-previa?mes=${anomes}`, { headers });
                    setPreviaPopulis(rp.ok ? await rp.json() : null);
                } catch { setPreviaPopulis(null); }
            } else {
                setPreviaPopulis(null);
            }

            // Fila de exceções para quem aprova (ESCRITORIO/Admin). Bloco acessório —
            // falha aqui não derruba a tela.
            if (ehEscritorio) {
                try {
                    const rx = await fetch(`/api/rh/destaque-mes/excecoes?mes=${anomes}`, { headers });
                    setExcecoesFila(rx.ok ? (await rx.json()).excecoes || [] : []);
                } catch { setExcecoesFila([]); }
            } else {
                setExcecoesFila([]);
            }
        } catch (e) {
            alert(e.message);
            setPopulacao([]); setEleitos([]); setSemEleito([]); setPopulacaoAtual([]);
            setCobertura(null); setCoberturaAviso(''); setPreviaPopulis(null);
            setEleicao(null); setEleicaoAviso(''); setExcecoesFila([]);
        } finally {
            setCarregando(false);
        }
    }, [anomes, mesAtual, selecionadoEhAtual, ehPrivilegiado, lojaFiltro, ehEscritorio]);

    useEffect(() => { buscar(); }, [buscar]);

    // Exclui quem JÁ é destaque neste mês/loja — no complemento, ele não pode reaparecer
    // na lista para ser eleito de novo (o backend também barra).
    const elegiveis = useMemo(() => {
        const eleitosIds = new Set(eleitos.map((e) => e.ID_FUNCIONARIO));
        return populacao.filter((p) => p.elegivel && !eleitosIds.has(p.idFuncionario));
    }, [populacao, eleitos]);
    const elegiveisAtual = useMemo(() => populacaoAtual.filter((p) => p.elegivel), [populacaoAtual]);

    // NÃO ELEGÍVEIS do mês — todos os que caíram em algum motivo (DEM-00064). Antes a
    // tela só mostrava dois recortes (carência e ausência de marcação) em chips; agora é
    // UMA tabela com o motivo de cada um, que é também onde o gerente MARCA a exceção.
    // Os próprios `motivos` já trazem o detalhe (o mês da carência, os dias sem marcação),
    // então nada se perde ao unir. Exclui quem virou elegível POR exceção (esse já subiu
    // para a tabela de elegíveis com o selo "Exceção").
    const naoElegiveis = useMemo(
        () => populacao.filter((p) => !p.elegivel),
        [populacao]);

    // Troca o ano e, se o mês selecionado não existir nele (2026 começa em julho),
    // puxa para o primeiro mês válido — senão o select ficaria mostrando um mês que
    // não está nas opções e a consulta sairia com um período recusado pelo backend.
    const trocarAno = (novoAno) => {
        setAno(novoAno);
        const permitidos = mesesDisponiveis(novoAno);
        if (!permitidos.some((m) => m.v === mes)) setMes(permitidos[0].v);
    };

    const toggleSelecionado = (id) => {
        setSelecionados((prev) => {
            const s = new Set(prev);
            if (s.has(id)) s.delete(id);
            else if (s.size < vagas) s.add(id); // trava nas vagas restantes da loja
            return s;
        });
        // Os anexos de quem foi desmarcado ficam guardados de propósito: desmarcar por
        // engano e remarcar não deve custar reescolher os arquivos. Só o POST leva os
        // anexos dos que estão marcados.
    };

    // Guarda (ou troca) um anexo escolhido para uma pessoa. `tipo` = 'foto' | 'prova'.
    const definirAnexo = async (id, tipo, file) => {
        try {
            const dataUrl = tipo === 'foto'
                ? await lerImagemRedimensionada(file)   // foto sempre passa pelo resize
                : await prepararAnexoProva(file);       // prova: imagem redimensiona, PDF vai cru
            setAnexos((prev) => ({
                ...prev,
                [id]: { ...(prev[id] || {}), [tipo]: dataUrl, [`${tipo}Nome`]: file.name },
            }));
        } catch (e) {
            alert(e.message);
        }
    };

    // Liga/desliga o reaproveitamento da foto anterior. Ligar LIMPA o arquivo que
    // porventura já tinha sido escolhido (os dois caminhos são exclusivos — mandar os
    // dois ao backend deixaria ambíguo qual vale).
    const onReaproveitarFoto = (id, ligar) => {
        setAnexos((prev) => {
            const atual = { ...(prev[id] || {}) };
            if (ligar) {
                atual.reaproveitarFoto = true;
                delete atual.foto;
                delete atual.fotoNome;
            } else {
                delete atual.reaproveitarFoto;
            }
            return { ...prev, [id]: atual };
        });
    };

    // Um escolhido só está pronto com os DOIS anexos — é a regra que o backend impõe
    // e que a tela precisa refletir antes de deixar clicar (senão o gerente monta 4
    // pessoas, envia 20 MB e toma 400 no fim).
    // A foto conta como resolvida por qualquer um dos dois caminhos: arquivo escolhido
    // OU reaproveitamento da eleição anterior.
    const completo = (id) => {
        const a = anexos[id];
        return Boolean(a && (a.foto || a.reaproveitarFoto) && a.prova);
    };
    const prontos = useMemo(
        () => [...selecionados].filter((id) => completo(id)).length,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selecionados, anexos]);
    // O limite é TETO: vale de 1 até as VAGAS restantes, todos com os dois anexos.
    // Zero selecionados não é "pronto" — para fechar sem destaque há botão próprio.
    const tudoPronto = vagas != null && selecionados.size >= 1
        && selecionados.size <= vagas && prontos === selecionados.size;

    // Encerra (ou reabre) a eleição do mês selecionado. Ato do RH central: fecha o mês
    // ANTES do fim da janela, e a partir daí nenhuma loja elege. Reabrir existe porque
    // um clique errado no dia 1º tiraria a rede inteira da eleição do mês.
    const alterarEleicao = async (acao) => {
        const encerrar = acao === 'encerrar';
        const pergunta = encerrar
            ? `Encerrar a eleição de ${anomes} agora? As lojas que ainda não elegeram NÃO vão mais conseguir, mesmo dentro do prazo.`
            : `Reabrir a eleição de ${anomes}? As lojas voltam a eleger enquanto a janela estiver aberta.`;
        if (!window.confirm(pergunta)) return;
        // Motivo é opcional (o backend aceita nulo) — cancelar o prompt não cancela o ato.
        const motivo = window.prompt('Motivo (opcional):', '') || null;
        setAlterandoEleicao(true);
        try {
            const token = localStorage.getItem('authToken');
            const res = await fetch(`/api/rh/destaque-mes/${encerrar ? 'encerrar' : 'reabrir'}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ mes: anomes, motivo }),
            });
            const dados = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(dados.message || `Erro ${res.status}`);
            alert(dados.message);
            await buscar();
        } catch (e) {
            alert(e.message);
        } finally {
            setAlterandoEleicao(false);
        }
    };

    // `semDestaque` = fechar o mês da loja sem eleger ninguém (lista vazia no POST).
    const eleger = async (semDestaque = false) => {
        if (!podeEleger) return;
        if (semDestaque) {
            if (selecionados.size > 0) return;
            if (!window.confirm(`Fechar ${anomes} de ${lojaEmFoco} SEM nenhum destaque? A decisão é DEFINITIVA — não poderá ser alterada.`)) return;
        } else {
            if (!tudoPronto) return;
            if (!window.confirm(`Confirmar ${selecionados.size} destaque(s) de ${lojaEmFoco} em ${anomes}? A eleição é DEFINITIVA — não poderá ser alterada.`)) return;
        }
        setSalvando(true);
        try {
            const token = localStorage.getItem('authToken');
            // Um POST só, com os anexos junto: o backend valida tudo, sobe ao SharePoint
            // e só então grava — assim não existe destaque sem foto/prova em instante
            // nenhum. Se qualquer upload falhar, NADA é gravado e a eleição se refaz.
            const res = await fetch('/api/rh/destaque-mes/eleger', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mes: anomes,
                    loja: lojaEmFoco,
                    destaques: (semDestaque ? [] : [...selecionados]).map((id) => (
                        anexos[id].reaproveitarFoto
                            // Sem `foto`: o servidor copia a da eleição anterior.
                            ? { id, reaproveitarFoto: true, prova: anexos[id].prova }
                            : { id, foto: anexos[id].foto, prova: anexos[id].prova }
                    )),
                }),
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(j.message || `Erro ${res.status}`);
            alert(j.message || 'Destaques registrados!');
            await buscar();
        } catch (e) {
            alert(e.message);
        } finally {
            setSalvando(false);
        }
    };

    // EXCEÇÃO (DEM-00064) — o gerente marca um inelegível (observação obrigatória). Fica
    // PENDENTE até o ESCRITORIO aprovar. Usa prompt nativo (mesma linha do window.confirm
    // da eleição) por ser um texto curto e obrigatório.
    const marcarExcecao = async (p) => {
        const obs = window.prompt(
            `Tornar ${p.nome} elegível por EXCEÇÃO (apesar de: ${p.motivos.join(', ')}).\n\n`
            + 'Justifique (obrigatório) — o escritório vai ler isto para aprovar:');
        if (obs == null) return;               // cancelou
        if (obs.trim().length < 3) { alert('A observação é obrigatória.'); return; }
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/rh/destaque-mes/excecao', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ mes: anomes, loja: p.loja, idFuncionario: p.idFuncionario, observacao: obs.trim() }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || `Erro ${r.status}`);
            alert(j.message || 'Exceção registrada.');
            await buscar();
        } catch (e) { alert(e.message); }
    };

    // EXCEÇÃO — o ESCRITORIO decide (aprova/rejeita) uma exceção pendente. Aprovar pede
    // confirmação; rejeitar aceita um motivo opcional.
    const decidirExcecao = async (ex, acao) => {
        let observacao = '';
        if (acao === 'APROVAR') {
            if (!window.confirm(`Aprovar a exceção de ${ex.NOME} (${ex.LOJA})? Ele passará a ser elegível em ${ex.ANOMES}.`)) return;
        } else {
            const motivo = window.prompt(`Rejeitar a exceção de ${ex.NOME} (${ex.LOJA}).\n\nMotivo (opcional):`);
            if (motivo == null) return;        // cancelou
            observacao = motivo.trim();
        }
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch(`/api/rh/destaque-mes/excecao/${ex.ID}/decidir`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ acao, observacao }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || `Erro ${r.status}`);
            alert(j.message || 'Decisão registrada.');
            await buscar();
        } catch (e) { alert(e.message); }
    };

    // Baixa o TXT do POPULIS. Vai por fetch (e não por <a href>) porque a rota exige o
    // token no header — um link direto tomaria 401.
    const baixarTxtPopulis = async (competencia) => {
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch(`/api/rh/destaque-mes/exportar-populis?mes=${anomes}&competencia=${competencia}`,
                { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || `Erro ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Destaque_Mes_${anomes}.txt`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        } catch (e) {
            alert(e.message);
        }
    };

    // Aviso quando o espelho do mês selecionado parece não ter sido carregado.
    const espelhoSuspeito = !carregando && !vistaSoCards && populacao.length > 0
        && populacao.every((p) => (p.diasEspelho || 0) < 5);
    const espelhoVazio = !carregando && !vistaSoCards && populacao.length === 0;

    const cor = '#d81d1d';

    // Props comuns das duas tabelas de elegíveis (a do mês fechado e a da prévia).
    const propsTabela = {
        lojaEmFoco, mostrarLoja: ehPrivilegiado && !lojaFiltro, limite: vagas,
        selecionados, anexos, onToggle: toggleSelecionado, onEscolherAnexo: definirAnexo,
        onReaproveitarFoto,
    };

    return (
        <div>
            {/* Cabeçalho da tela */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                <div style={{ padding: 10, borderRadius: 12, background: '#fedbdb', color: cor }}><Award size={26} /></div>
                <div>
                    <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#0f172a' }}>Destaque do Mês</h2>
                    <p style={{ margin: 0, color: '#8b6464', fontSize: 14 }}>
                        Funcionários sem falta, atestado, atraso ou ausência de marcação no mês — candidatos a destaque da loja.
                    </p>
                </div>
            </div>

            {/* Filtros de período (+ loja para privilegiados) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', margin: '20px 0 8px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#8b6464' }}>
                    ANO
                    <select value={ano} onChange={(e) => trocarAno(Number(e.target.value))} style={selectStyle}>
                        {anosDisponiveis().map((a) => <option key={a} value={a}>{a}</option>)}
                    </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#8b6464' }}>
                    MÊS
                    <select value={mes} onChange={(e) => setMes(Number(e.target.value))} style={selectStyle}>
                        {mesesDisponiveis(ano).map((m) => <option key={m.v} value={m.v}>{m.nome}</option>)}
                    </select>
                </label>
                {ehPrivilegiado && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#8b6464' }}>
                        LOJA
                        <select value={lojaFiltro} onChange={(e) => setLojaFiltro(e.target.value)} style={selectStyle}>
                            <option value="">Todas</option>
                            {LOJAS.map((l) => <option key={l} value={l}>{l}</option>)}
                        </select>
                    </label>
                )}
                {lojaEmFoco && <span style={{ fontSize: 13, color: '#8b6464', paddingBottom: 8 }}>
                    Loja <strong>{lojaEmFoco}</strong> · elege <strong>até {limiteDestaques(lojaEmFoco)}</strong> destaque(s)
                </span>}
            </div>

            {/* Informativo FIXO do prazo. Fica visível sempre (não só fora da janela):
                é a regra que o gerente precisa ter na cabeça o mês inteiro para não
                perder os 3 dias. Muda de tom quando a janela está aberta. */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 16px',
                padding: '12px 16px', borderRadius: 10, fontSize: 14,
                background: janelaAberta ? '#f0fdf4' : '#fdf4f4',
                border: `1px solid ${janelaAberta ? '#bbf7d0' : '#f0e2e2'}`,
                color: janelaAberta ? '#166534' : '#8b6464',
            }}>
                <CalendarClock size={18} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>
                    A eleição só fica disponível durante a <strong>janela mensal</strong> — {JANELA_TEXTO}.
                    {janelaExcecaoEsseMes && <strong> Neste mês, por exceção, o dia 3 vai até as 16h.</strong>}
                    {janelaAberta
                        ? ' A janela está aberta agora.'
                        : ' Fora dela, a lista de elegíveis segue disponível para consulta.'}
                </span>
                {/* ENCERRAR ANTES DO PRAZO — só RH/Admin/Diretoria, e só com a janela
                    aberta (fora dela a própria janela já barra a eleição). Fica AQUI, no
                    informativo do prazo, porque é o mesmo assunto: quando a rede pode
                    eleger. Reabrir aparece no lugar do encerrar quando o mês está fechado. */}
                {podeEncerrar && (
                    <button
                        onClick={() => alterarEleicao(eleicaoEncerrada ? 'reabrir' : 'encerrar')}
                        disabled={alterandoEleicao}
                        title={eleicaoEncerrada
                            ? 'Devolve a eleição às lojas enquanto a janela estiver aberta.'
                            : 'Fecha a eleição deste mês agora — nenhuma loja elege mais.'}
                        style={{
                            flexShrink: 0, padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                            cursor: alterandoEleicao ? 'not-allowed' : 'pointer',
                            border: `1px solid ${eleicaoEncerrada ? '#bbf7d0' : '#f0e2e2'}`,
                            background: '#fff', color: eleicaoEncerrada ? '#166534' : '#b91c1c',
                        }}
                    >
                        {alterandoEleicao
                            ? 'Aguarde…'
                            : (eleicaoEncerrada ? 'Reabrir eleição' : `Encerrar eleição de ${anomes}`)}
                    </button>
                )}
            </div>

            {/* ELEIÇÃO ENCERRADA pelo RH antes do prazo. Cinza-âmbar, não vermelho: não é
                erro nem pendência — é uma decisão tomada, e o banner diz de quem foi. */}
            {!carregando && eleicaoEncerrada && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '16px 0', padding: '14px 16px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: 14 }}>
                    <Ban size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <strong>Eleição encerrada.</strong>
                        <div style={{ marginTop: 4 }}>{eleicaoAviso}</div>
                        {eleicao && eleicao.motivo && (
                            <div style={{ marginTop: 4, fontSize: 13 }}>Motivo: {eleicao.motivo}</div>
                        )}
                    </div>
                </div>
            )}

            {carregando && <p style={{ color: '#8b6464' }}>Carregando…</p>}

            {/* ESPELHO INCOMPLETO — bloqueio da eleição. É o conserto do incidente de
                2026-08-04: julho foi carregado com 27 dos 31 dias e a rede inteira
                elegeu em cima disso, com uma eleita virando inelegível depois da
                recarga (e a eleição é definitiva). Vermelho, não âmbar: aqui nada
                acontece até alguém recarregar o CSV. */}
            {!carregando && cobertura && !cobertura.completo && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '16px 0', padding: '14px 16px', borderRadius: 10, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 14 }}>
                    <Ban size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <strong>Eleição bloqueada — espelho de ponto incompleto.</strong>
                        <div style={{ marginTop: 4 }}>{coberturaAviso}</div>
                        <div style={{ marginTop: 6, fontSize: 13 }}>
                            Peça ao RH a recarga do espelho de {anomes} e recarregue esta página.
                        </div>
                    </div>
                </div>
            )}

            {espelhoVazio && (
                <Aviso icon={<AlertTriangle size={18} />}>
                    Nenhum registro de ponto encontrado para {anomes}. O espelho deste mês pode ainda não ter sido carregado.
                </Aviso>
            )}
            {espelhoSuspeito && (
                <Aviso icon={<AlertTriangle size={18} />}>
                    O espelho de {anomes} tem pouquíssimos dias registrados — os números podem estar incompletos (carga parcial).
                </Aviso>
            )}

            {/* LOJA QUE FECHOU O MÊS SEM DESTAQUE — decisão registrada, não é pendência. */}
            {jaSemEleito && (
                <div style={{ margin: '20px 0', padding: '14px 16px', borderRadius: 10, background: '#f8fafc', border: '1px solid #cbd5e1', color: '#334155', fontSize: 14 }}>
                    <strong>Mês fechado sem destaque.</strong>{' '}
                    {semEleito.map((s) => `${s.LOJA} (por ${s.ELEITO_POR_NOME || '—'})`).join(' · ')}
                </div>
            )}

            {/* DESTAQUES JÁ ELEITOS — cards com foto (o Marketing extrai daqui) */}
            {eleitos.length > 0 && (
                <div style={{ margin: '20px 0', padding: 20, borderRadius: 14, background: 'linear-gradient(135deg,#fff7ed,#fef3c7)', border: '1px solid #fcd34d' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                        <Trophy size={20} color="#b45309" />
                        <strong style={{ color: '#92400e', fontSize: 16 }}>Destaques eleitos {ehPrivilegiado && !lojaFiltro ? '' : `— ${anomes}`}</strong>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                        {eleitos.map((e) => (
                            <CardDestaque key={e.ID} eleito={e} podeAnexar={podeFoto} />
                        ))}
                    </div>
                    {podeFoto && (
                        <p style={{ margin: '14px 0 0', fontSize: 12, color: '#92400e' }}>
                            Anexe a <strong>foto</strong> de cada destaque (o Marketing usa na divulgação) e, se houver, a <strong>prova</strong> (imagem ou PDF).
                        </p>
                    )}
                </div>
            )}

            {/* EXCEÇÕES AGUARDANDO APROVAÇÃO (DEM-00064) — só para quem decide
                (ESCRITORIO/Admin). Fica no topo para ser a 1ª coisa que o escritório vê. */}
            {ehEscritorio && excecoesFila.length > 0 && (
                <div style={{ margin: '20px 0', padding: 16, borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, color: '#1e40af', fontWeight: 800, fontSize: 15 }}>
                        <AlertTriangle size={18} /> Exceções aguardando aprovação ({excecoesFila.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {excecoesFila.map((ex) => (
                            <div key={ex.ID} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10, background: '#fff', border: '1px solid #dbeafe', borderRadius: 10, padding: '10px 14px' }}>
                                <div style={{ minWidth: 220, flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: '#0f172a' }}>{ex.NOME} · {ex.LOJA}</div>
                                    <div style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>{ex.CARGO || '—'} · {ex.ANOMES}</div>
                                    <div style={{ fontSize: 13, color: '#334155', marginTop: 4 }}><strong>Justificativa:</strong> {ex.OBSERVACAO}</div>
                                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Solicitado por {ex.SOLICITANTE_NOME || '—'}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={() => decidirExcecao(ex, 'APROVAR')}
                                        style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', background: '#16a34a', color: '#fff' }}>Aprovar</button>
                                    <button onClick={() => decidirExcecao(ex, 'REJEITAR')}
                                        style={{ padding: '8px 16px', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', background: '#fff', border: '1px solid #dc2626', color: '#b91c1c' }}>Rejeitar</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* TXT DE IMPORTAÇÃO NO POPULIS — só RH/Admin/Diretoria, e só em mês fechado.
                É o gatilho do PAGAMENTO do prêmio; não é do gerente da loja. */}
            {ehPrivilegiado && previaPopulis && previaPopulis.totalLinhas > 0 && (
                <ExportaPopulis previa={previaPopulis} anomes={anomes} onBaixar={baixarTxtPopulis} />
            )}

            {/* ANTES DE ELEGER — elegíveis em dois blocos (mês selecionado + prévia do atual) */}
            {!carregando && !vistaSoCards && (
                <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 800, color: '#166534', margin: 0 }}>
                            <CheckCircle2 size={20} /> Elegíveis — {anomes} ({elegiveis.length})
                        </h3>
                        {podeMarcarMais && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <span style={{ fontSize: 13, color: '#8b6464' }}>
                                    Selecionados <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{selecionados.size}/{vagas}</strong>
                                    {' · '}com anexos <strong style={{ fontVariantNumeric: 'tabular-nums', color: tudoPronto ? '#166534' : '#b45309' }}>{prontos}/{selecionados.size}</strong>
                                </span>
                                {/* Sem ninguém marcado, a saída é fechar o mês sem destaque —
                                    só na 1ª eleição (loja que já tem destaque não fecha "sem"). */}
                                {selecionados.size === 0 && eleitos.length === 0 && (
                                    <button
                                        onClick={() => eleger(true)}
                                        disabled={salvando}
                                        title="Nenhum funcionário elegível ou nenhum merece o destaque: registra o mês da loja sem destaque."
                                        style={{
                                            padding: '10px 18px', borderRadius: 8, fontWeight: 700, fontSize: 14,
                                            cursor: salvando ? 'not-allowed' : 'pointer',
                                            border: '1px solid #d81d1d', background: '#fff', color: '#b91c1c',
                                        }}
                                    >Fechar sem destaque</button>
                                )}
                                <button
                                    onClick={() => eleger(false)}
                                    disabled={salvando || !tudoPronto}
                                    title={tudoPronto ? '' : 'Selecione os destaques e anexe a foto e a prova de cada um.'}
                                    style={{
                                        padding: '10px 22px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 14,
                                        cursor: (salvando || !tudoPronto) ? 'not-allowed' : 'pointer',
                                        background: tudoPronto ? 'linear-gradient(135deg,#f63b3b,#d81d1d)' : '#e5d3d3',
                                        color: tudoPronto ? '#fff' : '#a08a8a',
                                    }}
                                >{salvando ? 'Enviando…' : (eleitos.length > 0 ? 'Eleger mais' : 'Eleger destaques')}</button>
                            </div>
                        )}
                    </div>
                    {/* Nota quando o mês selecionado é o atual (em andamento): não elege. */}
                    {selecionadoEhAtual && (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#8b6464' }}>
                            Mês em andamento — espelho parcial. A eleição abre quando o mês fechar.
                        </p>
                    )}
                    {/* Regra nova: os anexos entram ANTES de confirmar. Dito aqui e não
                        só no tooltip do botão porque muda o preparo do gerente — ele
                        precisa das fotos e das provas em mãos ao abrir a tela. */}
                    {podeMarcarMais && (
                        <p style={{ margin: '8px 0 0', fontSize: 13, color: '#8b6464' }}>
                            {eleitos.length > 0 && (
                                <strong>A loja já tem {eleitos.length} destaque(s); dá para eleger mais {vagas}. </strong>
                            )}
                            Marque <strong>até {vagas}</strong> funcionário(s) (pode ser menos, se o mês teve poucos elegíveis) e anexe, para cada um, a <strong>foto</strong> de divulgação e a
                            <strong> prova</strong> (imagem ou PDF). A eleição só é aceita com os dois anexos —
                            depois dela não há como incluir nem trocar quem foi eleito.
                        </p>
                    )}
                    <TabelaElegiveis {...propsTabela} lista={elegiveis} selecionavel={podeMarcarMais} />

                    {/* NÃO ELEGÍVEIS — uma tabela com o motivo de cada um, e o lugar onde o
                        gerente MARCA a exceção (DEM-00064). Substitui os antigos chips de
                        carência e de ausência de marcação: o `motivos` de cada pessoa já traz
                        o detalhe (o mês da carência, os dias sem marcação). Aprovada a exceção,
                        a pessoa sobe para a tabela de elegíveis acima, com o selo "Exceção". */}
                    {naoElegiveis.length > 0 && (
                        <div style={{ marginTop: 24 }}>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: '#8b6464', margin: 0 }}>
                                <Ban size={18} /> Não elegíveis — {anomes} ({naoElegiveis.length})
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#8b6464' }}>
                                Caíram em algum critério do mês. <strong>Carência</strong> é rodízio (quem foi destaque não repete);
                                {' '}<strong>ausência de marcação</strong> são dias em que o ponto não fechou (o RH precisa acertar).
                                {podeMarcarExcecao && ' Para reconhecer alguém mesmo assim, marque a exceção — um usuário do escritório aprova.'}
                            </p>
                            <TabelaNaoElegiveis
                                lista={naoElegiveis}
                                mostrarLoja={ehPrivilegiado && !lojaFiltro}
                                podeMarcar={podeMarcarExcecao}
                                onMarcar={marcarExcecao}
                            />
                        </div>
                    )}

                    {/* Bloco de prévia do mês atual (só quando o selecionado não é o atual) */}
                    {!selecionadoEhAtual && (
                        <div style={{ marginTop: 28 }}>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: '#8b6464', margin: 0 }}>
                                <CheckCircle2 size={18} /> Prévia — mês atual {mesAtual} ({elegiveisAtual.length})
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: 13, color: '#8b6464' }}>
                                Em andamento (espelho parcial) — quem está limpo até agora. Não elege ainda.
                            </p>
                            <TabelaElegiveis {...propsTabela} lista={elegiveisAtual} selecionavel={false} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Estilos da tabela de elegíveis (nível de módulo — não mudam entre renders).
const th = { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 800, color: '#8b6464', textTransform: 'uppercase', borderBottom: '2px solid #f0e2e2' };
const td = { padding: '10px 12px', fontSize: '14px', color: '#0f172a', borderBottom: '1px solid #f6eeee' };
const numTd = { ...td, textAlign: 'center', fontVariantNumeric: 'tabular-nums' };

// Tabela de elegíveis, reutilizada nos dois blocos (mês fechado + prévia do atual).
// `selecionavel` liga os checkboxes de eleição e a coluna de anexos — só no bloco do
// mês fechado, quando a eleição está liberada.
//
// ⚠️ MORA NO NÍVEL DO MÓDULO de propósito. Enquanto era declarada DENTRO do componente
// pai, cada render criava um tipo novo e o React desmontava e remontava a subárvore
// inteira. Isso passou a importar em 2026-08-06, quando a coluna de anexos trouxe
// <input type="file"> para dentro dela: input remontado no meio da escolha do arquivo
// perde o evento de seleção, e o gerente clica sem nada acontecer.
const TabelaElegiveis = ({
    lista, selecionavel, lojaEmFoco, mostrarLoja, limite,
    selecionados, anexos, onToggle, onEscolherAnexo, onReaproveitarFoto,
}) => {
    if (lista.length === 0) {
        return <p style={{ color: '#8b6464', marginTop: 12 }}>Nenhum funcionário elegível{lojaEmFoco ? ` em ${lojaEmFoco}` : ''}.</p>;
    }
    return (
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
                <thead><tr>
                    {selecionavel && <th style={{ ...th, width: 40 }} />}
                    <th style={th}>Funcionário</th>
                    <th style={th}>Cargo</th>
                    {mostrarLoja && <th style={th}>Loja</th>}
                    <th style={{ ...th, textAlign: 'center' }}>Dias de casa</th>
                    {/* Anexos: só na tabela que elege. Foto e prova são obrigatórias
                        para confirmar — ver o comentário no topo do arquivo. */}
                    {selecionavel && <th style={th}>Foto e prova (obrigatórias)</th>}
                </tr></thead>
                <tbody>
                    {lista.map((p) => {
                        const marcado = selecionavel && selecionados.has(p.idFuncionario);
                        const bloqueiaMarcar = selecionavel && !marcado && selecionados.size >= limite;
                        return (
                            <tr key={p.idFuncionario} style={{ background: marcado ? '#f0fdf4' : 'transparent' }}>
                                {selecionavel && (
                                    <td style={{ ...td, textAlign: 'center' }}>
                                        <input type="checkbox" checked={marcado} disabled={bloqueiaMarcar}
                                            onChange={() => onToggle(p.idFuncionario)} style={{ width: 18, height: 18, cursor: bloqueiaMarcar ? 'not-allowed' : 'pointer' }} />
                                    </td>
                                )}
                                <td style={{ ...td, fontWeight: 600 }}>
                                    {p.nome}
                                    {/* Elegível POR exceção (DEM-00064): deixa explícito na tabela. */}
                                    {p.excecao && p.excecao.elegivelPorExcecao && (
                                        <span style={{ marginLeft: 8 }}><SeloExcecao excecao={p.excecao} /></span>
                                    )}
                                </td>
                                <td style={td}>{p.cargo || '—'}</td>
                                {mostrarLoja && <td style={td}>{p.loja}</td>}
                                <td style={numTd}>{p.tempoEmpresaDias ?? '—'}</td>
                                {selecionavel && (
                                    <td style={td}>
                                        {marcado ? (
                                            <SeletorAnexos
                                                id={p.idFuncionario}
                                                anexo={anexos[p.idFuncionario]}
                                                fotoAnterior={p.fotoAnterior}
                                                onEscolher={onEscolherAnexo}
                                                onReaproveitar={onReaproveitarFoto}
                                            />
                                        ) : (
                                            <span style={{ fontSize: 12, color: '#c0aeae' }}>marque para anexar</span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

// Selo do estado da exceção (DEM-00064): pendente/aprovada/rejeitada. A observação vai no
// title (tooltip) — é o que o gerente escreveu e o escritório leu para decidir.
const SeloExcecao = ({ excecao }) => {
    const mapa = {
        PENDENTE: { txt: 'Exceção pendente', bg: '#eff6ff', bd: '#bfdbfe', cor: '#1e40af' },
        APROVADA: { txt: 'Exceção', bg: '#ecfdf5', bd: '#a7f3d0', cor: '#047857' },
        REJEITADA: { txt: 'Exceção rejeitada', bg: '#fef2f2', bd: '#fecaca', cor: '#b91c1c' },
    };
    const s = mapa[excecao.status] || mapa.PENDENTE;
    const titulo = [excecao.observacao, excecao.aprovadorNome && `Aprovada por ${excecao.aprovadorNome}`]
        .filter(Boolean).join(' · ');
    return (
        <span title={titulo}
            style={{ fontSize: 12, fontWeight: 700, background: s.bg, border: `1px solid ${s.bd}`, color: s.cor, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap' }}>
            {s.txt}
        </span>
    );
};

// Tabela de NÃO elegíveis (DEM-00064): o motivo de cada um + o controle de exceção. Se já
// há exceção (pendente/rejeitada), mostra o selo; senão (ou se foi rejeitada) o gerente
// pode marcar, quando `podeMarcar` (mês fechado, janela aberta, espelho inteiro).
const TabelaNaoElegiveis = ({ lista, mostrarLoja, podeMarcar, onMarcar }) => (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead><tr>
                <th style={th}>Funcionário</th>
                <th style={th}>Cargo</th>
                {mostrarLoja && <th style={th}>Loja</th>}
                <th style={th}>Por quê?</th>
                <th style={{ ...th, textAlign: 'right' }}>Exceção</th>
            </tr></thead>
            <tbody>
                {lista.map((p) => {
                    const rejeitada = p.excecao && p.excecao.status === 'REJEITADA';
                    return (
                        <tr key={p.idFuncionario}>
                            <td style={{ ...td, fontWeight: 600 }}>{p.nome}</td>
                            <td style={td}>{p.cargo || '—'}</td>
                            {mostrarLoja && <td style={td}>{p.loja}</td>}
                            <td style={{ ...td, color: '#8b6464' }}>{(p.motivos || []).join(', ') || '—'}</td>
                            <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {p.excecao && <SeloExcecao excecao={p.excecao} />}
                                {(!p.excecao || rejeitada) && podeMarcar && (
                                    <button onClick={() => onMarcar(p)}
                                        style={{ marginLeft: p.excecao ? 8 : 0, padding: '6px 12px', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', background: '#fff', border: '1px solid #2563eb', color: '#1d4ed8' }}>
                                        {rejeitada ? 'Marcar de novo' : 'Marcar exceção'}
                                    </button>
                                )}
                                {!p.excecao && !podeMarcar && <span style={{ fontSize: 12, color: '#c0aeae' }}>—</span>}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    </div>
);

// Escolha dos DOIS anexos de um candidato marcado, ANTES de eleger. Os arquivos ficam
// só na memória da tela (data URI) — nada sobe ao SharePoint até o POST /eleger, para
// não deixar lixo lá quando o gerente muda de ideia sobre quem eleger.
//
// Quando a pessoa JÁ FOI destaque e tem foto guardada, a foto tem um terceiro caminho:
// **reaproveitar**. Nesse caso nada trafega do celular do gerente — o servidor copia a
// foto anterior para o mês novo. A PROVA não tem esse caminho: ela é o comprovante
// daquele mês, e repetir seria anexar evidência de um mês noutro.
const SeletorAnexos = ({ id, anexo, fotoAnterior, onEscolher, onReaproveitar }) => {
    const refFoto = useRef(null);
    const [ocupado, setOcupado] = useState('');

    const escolher = async (tipo, ev) => {
        const file = ev.target.files && ev.target.files[0];
        ev.target.value = ''; // permite reescolher o mesmo arquivo
        if (!file) return;
        await escolherArquivo(tipo, file);
    };

    // Mesma coisa, recebendo o File pronto — é o que o EscolherAnexo entrega.
    const escolherArquivo = async (tipo, file) => {
        if (!file) return;
        setOcupado(tipo);
        try { await onEscolher(id, tipo, file); } finally { setOcupado(''); }
    };

    // Um botão por anexo: verde quando o arquivo já está escolhido (com o nome dele,
    // que é como o gerente confere se pegou o arquivo certo), âmbar enquanto falta.
    const pill = (pronto) => ({
        fontSize: 12, fontWeight: 700, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 5, maxWidth: 210,
        border: `1px solid ${pronto ? '#bbf7d0' : '#fcd34d'}`,
        background: pronto ? '#f0fdf4' : '#fffbeb',
        color: pronto ? '#166534' : '#92400e',
    });
    const rotulo = (pronto, nome, padrao) => (
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pronto ? nome : padrao}
        </span>
    );

    const reaproveitando = Boolean(anexo && anexo.reaproveitarFoto);
    // A foto está resolvida por um dos dois caminhos: arquivo escolhido OU reaproveitada.
    const temFoto = Boolean(anexo && anexo.foto) || reaproveitando;
    const temProva = Boolean(anexo && anexo.prova);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <input ref={refFoto} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={(e) => escolher('foto', e)} />
            <button type="button" onClick={() => refFoto.current && refFoto.current.click()}
                disabled={ocupado === 'foto'} style={pill(temFoto && !reaproveitando)}>
                {temFoto && !reaproveitando ? <CheckCircle2 size={13} /> : <Camera size={13} />}
                {ocupado === 'foto'
                    ? 'Lendo…'
                    : rotulo(Boolean(anexo && anexo.foto), anexo && anexo.fotoNome,
                        reaproveitando ? 'Enviar outra foto' : 'Escolher foto')}
            </button>

            {/* Reaproveitar a foto da última eleição — só aparece para quem já foi
                destaque com foto. Poupa o gerente de reenviar a mesma imagem pelo 4G
                da loja, que é o momento em que o anexo mais escapava. */}
            {fotoAnterior && (
                <button type="button" onClick={() => onReaproveitar(id, !reaproveitando)}
                    style={{ ...pill(reaproveitando), padding: '4px 8px' }}
                    title={`Foto usada em ${fotoAnterior.anomes} (${fotoAnterior.loja})`}>
                    {reaproveitando ? <CheckCircle2 size={13} /> : <MiniaturaFoto id={fotoAnterior.id} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {reaproveitando ? `Usando a de ${fotoAnterior.anomes}` : `Repetir a de ${fotoAnterior.anomes}`}
                    </span>
                </button>
            )}

            {/* A prova aceita foto OU PDF — e é por isso que ela usa o
                EscolherAnexo (dois gatilhos) em vez de um input só: com
                `accept` misto o Android não oferece a câmera. A foto acima
                fica no input simples porque `image/*` puro já traz câmera. */}
            <EscolherAnexo
                accept="image/*,application/pdf"
                cor="#d97706"
                compacto
                ocupado={ocupado === 'prova'}
                nome={temProva ? ((anexo && anexo.provaNome) || 'Prova anexada') : ''}
                rotulo="Escolher prova"
                onArquivo={(file) => escolherArquivo('prova', file)}
            />
        </div>
    );
};

// Miniatura da foto anterior, para o gerente ver O QUE está repetindo antes de
// confirmar. Baixa pelo proxy autenticado (a webUrl do SharePoint não carrega num
// <img> direto).
//
// ⚠️ Falha em silêncio de propósito: o proxy tem recorte por loja, então a foto de
// alguém que veio transferido de OUTRA unidade dá 403 para este gerente. Nesse caso
// não há miniatura — mas o reaproveitamento continua valendo, porque quem copia o
// arquivo é o servidor. Perder a miniatura é cosmético; travar o botão não seria.
const MiniaturaFoto = ({ id }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let url; let vivo = true;
        (async () => {
            try {
                const token = localStorage.getItem('authToken');
                const r = await fetch(`/api/rh/destaque-mes/foto/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) throw new Error();
                url = URL.createObjectURL(await r.blob());
                if (vivo) setSrc(url); else URL.revokeObjectURL(url);
            } catch { /* sem miniatura — ver o comentário acima */ }
        })();
        return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
    }, [id]);

    if (!src) return <Camera size={13} />;
    return <img src={src} alt="" style={{ width: 18, height: 18, borderRadius: 3, objectFit: 'cover', flexShrink: 0 }} />;
};

// Bloco de extração do TXT de importação no POPULIS (RH/Admin/Diretoria).
//
// ⚠️ A extração é LIBERADA MESMO COM LOJA PENDENTE, e o bloco mostra quais faltam.
//    Travar em "todas elegeram" faria uma loja atrasada segurar o prêmio da rede
//    inteira — foi o que julho/2026 fez, com FER e RDP fora do prazo.
const ExportaPopulis = ({ previa, anomes, onBaixar }) => {
    // Competência = o mês em que o prêmio entra na FOLHA. O padrão é o mês apurado; o
    // RH troca quando o pagamento cai na competência seguinte (quem decide é o RH, e
    // isso muda de rodada para rodada — por isso o campo é editável e não constante).
    const [competencia, setCompetencia] = useState(previa.competenciaPadrao || anomes);
    const [baixando, setBaixando] = useState(false);
    const pendentes = previa.lojasPendentes || [];
    const total = (previa.lojasQueElegeram || []).length + pendentes.length;
    const valor = (previa.valorCentavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

    const baixar = async () => {
        setBaixando(true);
        try { await onBaixar(competencia); } finally { setBaixando(false); }
    };

    const linhaInfo = { fontSize: 13, color: '#334155', display: 'flex', gap: 6, alignItems: 'baseline' };
    const dado = { fontWeight: 700, fontVariantNumeric: 'tabular-nums' };

    return (
        <div style={{ margin: '20px 0', padding: 18, borderRadius: 14, background: '#f8fafc', border: '1px solid #cbd5e1' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <FileDown size={18} color="#334155" />
                <strong style={{ color: '#0f172a', fontSize: 15 }}>Importação no POPULIS — {anomes}</strong>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 28px', marginBottom: 12 }}>
                <span style={linhaInfo}>Destaques no arquivo: <span style={dado}>{previa.totalLinhas}</span></span>
                <span style={linhaInfo}>Lojas que elegeram: <span style={dado}>{(previa.lojasQueElegeram || []).length} de {total}</span></span>
                <span style={linhaInfo}>Verba: <span style={dado}>{previa.verba}</span></span>
                <span style={linhaInfo}>Valor por destaque: <span style={dado}>R$ {valor}</span></span>
            </div>

            {pendentes.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0 0 12px', padding: '10px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: 13 }}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                        <strong>{pendentes.length} loja(s) ainda não elegeram:</strong> {pendentes.join(', ')}.
                        O arquivo sai assim mesmo, só com quem já foi eleito — mas essas lojas ficarão de fora deste pagamento.
                    </span>
                </div>
            )}

            {(previa.semRegistro || []).length > 0 && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '0 0 12px', padding: '10px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: 13 }}>
                    <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    <span>
                        <strong>Sem matrícula (REGISTRO):</strong> {previa.semRegistro.join('; ')}.
                        O POPULIS casa a pessoa pela matrícula — a exportação fica bloqueada até isso ser corrigido.
                    </span>
                </div>
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                    COMPETÊNCIA (mês do pagamento)
                    <input type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)}
                        style={{ ...selectStyle, minWidth: 160 }} />
                </label>
                <button onClick={baixar} disabled={baixando}
                    style={{
                        padding: '10px 20px', borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 14,
                        background: '#334155', color: '#fff', cursor: baixando ? 'wait' : 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 7,
                    }}>
                    <Download size={15} /> {baixando ? 'Gerando…' : `Baixar TXT (${previa.totalLinhas} linhas)`}
                </button>
                <span style={{ fontSize: 12, color: '#64748b', paddingBottom: 10 }}>
                    Por padrão a competência é o mês apurado. Troque se o prêmio for lançado em outra folha.
                </span>
            </div>
        </div>
    );
};

// Card de um destaque eleito: exibe a FOTO (via proxy autenticado; o Marketing
// extrai daqui) e deixa anexar/trocar a foto e a PROVA (comprovante — imagem ou
// PDF, anexo independente da foto). Ambos os anexos vão pro SharePoint.
const CardDestaque = ({ eleito, podeAnexar }) => {
    const [temFoto, setTemFoto] = useState(Boolean(eleito.FOTO_URL));
    const [versao, setVersao] = useState(0);        // força re-fetch após troca
    const [src, setSrc] = useState(null);           // objectURL da imagem baixada
    const [carregandoFoto, setCarregandoFoto] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const inputRef = useRef(null);
    // Prova: só guardamos "tem prova?" — o arquivo (PDF/imagem) é baixado sob demanda
    // pelo botão "Ver prova" (não pré-carrega, para não pesar o card com PDFs).
    const [temProva, setTemProva] = useState(Boolean(eleito.PROVA_URL));
    const [enviandoProva, setEnviandoProva] = useState(false);
    const [abrindoProva, setAbrindoProva] = useState(false);

    // Baixa a foto pelo proxy do backend (a webUrl do SharePoint é autenticada, um
    // <img src=webUrl> não carregaria). Vira objectURL para o <img> e o botão baixar.
    useEffect(() => {
        if (!temFoto) { setSrc(null); return undefined; }
        let url; let vivo = true;
        setCarregandoFoto(true);
        (async () => {
            try {
                const token = localStorage.getItem('authToken');
                const r = await fetch(`/api/rh/destaque-mes/foto/${eleito.ID}`, { headers: { Authorization: `Bearer ${token}` } });
                if (!r.ok) throw new Error();
                const blob = await r.blob();
                url = URL.createObjectURL(blob);
                if (vivo) setSrc(url); else URL.revokeObjectURL(url);
            } catch {
                if (vivo) setSrc(null);
            } finally {
                if (vivo) setCarregandoFoto(false);
            }
        })();
        return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
    }, [eleito.ID, temFoto, versao]);

    const escolherArquivo = () => inputRef.current && inputRef.current.click();

    const aoSelecionarArquivo = async (ev) => {
        const file = ev.target.files && ev.target.files[0];
        ev.target.value = ''; // permite reescolher o mesmo arquivo
        if (!file) return;
        setEnviando(true);
        try {
            const dataUrl = await lerImagemRedimensionada(file);
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/rh/destaque-mes/foto', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: eleito.ID, imagem: dataUrl }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || `Erro ${r.status}`);
            setTemFoto(true);
            setVersao((v) => v + 1); // re-baixa a nova imagem
        } catch (e) {
            alert(e.message);
        } finally {
            setEnviando(false);
        }
    };

    // Anexa/troca a PROVA (imagem passa pelo resize; PDF vai cru). Campo do body:
    // `arquivo` (o backend usa esse nome para a prova, distinto do `imagem` da foto).
    // Recebe o File pronto do EscolherAnexo.
    const enviarProva = async (file) => {
        if (!file) return;
        setEnviandoProva(true);
        try {
            const dataUrl = await prepararAnexoProva(file);
            const token = localStorage.getItem('authToken');
            const r = await fetch('/api/rh/destaque-mes/prova', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: eleito.ID, arquivo: dataUrl }),
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j.message || `Erro ${r.status}`);
            setTemProva(true);
        } catch (e) {
            alert(e.message);
        } finally {
            setEnviandoProva(false);
        }
    };

    // Abre a prova numa nova aba (baixa pelo proxy autenticado → objectURL). Funciona
    // tanto para PDF quanto imagem. Revoga o objectURL depois de um tempo (a aba já
    // segurou o conteúdo); revogar na hora quebraria o carregamento.
    const verProva = async () => {
        setAbrindoProva(true);
        try {
            const token = localStorage.getItem('authToken');
            const r = await fetch(`/api/rh/destaque-mes/prova/${eleito.ID}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!r.ok) throw new Error('Não foi possível abrir a prova.');
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            alert(e.message);
        } finally {
            setAbrindoProva(false);
        }
    };

    const nomeArquivo = `destaque_${eleito.LOJA}_${String(eleito.NOME || '').replace(/\s+/g, '-')}.jpg`;
    const btn = { fontSize: 12, fontWeight: 700, padding: '6px 10px', borderRadius: 6, border: '1px solid #fcd34d', background: '#fff', color: '#92400e', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none' };

    return (
        <div style={{ width: 190, background: '#fff', borderRadius: 12, border: '1px solid #fcd34d', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {/* Área da foto (quadrada) */}
            <div style={{ width: '100%', aspectRatio: '1 / 1', background: '#fdf6e3', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d6c08a' }}>
                {carregandoFoto ? (
                    <Loader2 size={26} className="animate-spin" />
                ) : src ? (
                    <img src={src} alt={eleito.NOME} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                    <User size={40} />
                )}
            </div>
            {/* Identificação */}
            <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Star size={14} color="#d97706" fill="#fbbf24" />
                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 14 }}>{eleito.NOME}</span>
                </div>
                <span style={{ fontSize: 12, color: '#8b6464' }}>{eleito.LOJA} · {eleito.CARGO || '—'}</span>
            </div>
            {/* Ações da FOTO (divulgação) */}
            <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {podeAnexar && (
                    <>
                        <input ref={inputRef} type="file" accept="image/*" onChange={aoSelecionarArquivo} style={{ display: 'none' }} />
                        <button onClick={escolherArquivo} disabled={enviando} style={{ ...btn, cursor: enviando ? 'wait' : 'pointer' }}>
                            <Camera size={13} /> {enviando ? 'Enviando…' : (temFoto ? 'Trocar foto' : 'Adicionar foto')}
                        </button>
                    </>
                )}
                {src && (
                    <a href={src} download={nomeArquivo} style={btn}>
                        <Download size={13} /> Baixar
                    </a>
                )}
            </div>
            {/* Ações da PROVA (comprovante — imagem ou PDF, independente da foto) */}
            <div style={{ padding: '0 12px 12px', display: 'flex', gap: 6, flexWrap: 'wrap', borderTop: '1px dashed #fde68a', paddingTop: 8 }}>
                {podeAnexar && (
                    <>
                        <EscolherAnexo
                            accept="image/*,application/pdf"
                            cor="#d97706"
                            compacto
                            ocupado={enviandoProva}
                            rotulo={temProva ? 'Trocar prova' : 'Anexar prova'}
                            onArquivo={enviarProva}
                        />
                    </>
                )}
                {temProva && (
                    <button onClick={verProva} disabled={abrindoProva} style={{ ...btn, cursor: abrindoProva ? 'wait' : 'pointer' }}>
                        <Eye size={13} /> {abrindoProva ? 'Abrindo…' : 'Ver prova'}
                    </button>
                )}
                {!podeAnexar && !temProva && (
                    <span style={{ fontSize: 11, color: '#a8998a' }}>Sem prova anexada</span>
                )}
            </div>
        </div>
    );
};

const selectStyle = {
    padding: '9px 12px', borderRadius: 8, border: '1px solid #e5d3d3',
    fontSize: 14, color: '#0f172a', background: '#fff', minWidth: 130,
};

const Aviso = ({ icon, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0', padding: '12px 16px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', fontSize: 14 }}>
        {icon}<span>{children}</span>
    </div>
);

export default RHDestaqueMes;
